import { useId, useRef, useState, type DragEvent, type KeyboardEvent, type ReactNode } from "react";
import { ApiError, uploadsApi } from "../lib/api";
import { validateImageFile } from "../lib/imageFile";

/**
 * Cover / chapter image control: preview + Upload / Use URL / Remove.
 * Uploads go to the API (which stores them in the media bucket) and the
 * resulting URL is handed to `onChange`; saving is the parent form's job.
 *
 * Deliberately not wrapped in the shared <Field> (a <label>), since buttons
 * inside a label would trigger the file input.
 */
export function ImageField({
  label,
  value,
  onChange,
  storyId,
  hint,
  square = false,
}: {
  label: ReactNode;
  value: string | null;
  onChange: (url: string | null) => void;
  storyId: string;
  hint?: ReactNode;
  /** Compact 96px square preview (item icons) instead of the wide 16:9 box. */
  square?: boolean;
}) {
  const id = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [urlMode, setUrlMode] = useState(false);
  const [urlDraft, setUrlDraft] = useState(value ?? "");
  const [dragOver, setDragOver] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const busy = progress !== null;

  async function upload(file: File) {
    const problem = validateImageFile(file);
    if (problem) {
      setError(problem);
      return;
    }
    setError(null);
    setProgress(0);
    try {
      const res = await uploadsApi.image(storyId, file, setProgress);
      onChange(res.url);
      setUrlDraft(res.url);
      setUrlMode(false);
    } catch (e) {
      setError(e instanceof ApiError || e instanceof Error ? e.message : "Upload failed.");
    } finally {
      setProgress(null);
    }
  }

  function onPick(files: FileList | null) {
    const file = files?.[0];
    if (file) void upload(file);
    if (inputRef.current) inputRef.current.value = "";
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    if (busy) return;
    onPick(e.dataTransfer.files);
  }

  function commitUrl() {
    const next = urlDraft.trim();
    onChange(next || null);
    setUrlMode(false);
  }

  function onUrlKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      commitUrl();
    } else if (e.key === "Escape") {
      e.preventDefault();
      setUrlDraft(value ?? "");
      setUrlMode(false);
    }
  }

  return (
    <div className={`field image-field${square ? " square" : ""}`}>
      <span className="field-label" id={`${id}-label`}>
        {label}
      </span>

      <div
        className={["image-preview", dragOver ? "drag-over" : "", value ? "" : "empty"].join(" ")}
        onDragOver={(e) => {
          e.preventDefault();
          if (!busy) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        aria-labelledby={`${id}-label`}
      >
        {value ? (
          <img src={value} alt="" />
        ) : (
          <span className="muted small">{busy ? "Uploading…" : "No image. Drop one here or upload."}</span>
        )}
        {busy && (
          <div className="image-progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round((progress ?? 0) * 100)}>
            <div className="image-progress-bar" style={{ width: `${Math.round((progress ?? 0) * 100)}%` }} />
          </div>
        )}
      </div>

      <div className="image-controls">
        <input
          ref={inputRef}
          id={`${id}-file`}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          className="sr-only"
          onChange={(e) => onPick(e.target.files)}
          disabled={busy}
          aria-label="Choose an image to upload"
        />
        <button type="button" className="btn btn-sm" onClick={() => inputRef.current?.click()} disabled={busy}>
          {value ? "Replace" : "Upload"}
        </button>
        <button
          type="button"
          className="btn btn-sm btn-ghost"
          onClick={() => {
            setUrlDraft(value ?? "");
            setUrlMode((m) => !m);
          }}
          disabled={busy}
          aria-expanded={urlMode}
        >
          Use URL
        </button>
        {value && (
          <button
            type="button"
            className="btn btn-sm btn-ghost"
            onClick={() => {
              onChange(null);
              setUrlDraft("");
              setUrlMode(false);
              setError(null);
            }}
            disabled={busy}
          >
            Remove
          </button>
        )}
      </div>

      {urlMode && (
        <input
          type="url"
          className="image-url-input"
          value={urlDraft}
          placeholder="https://…"
          autoFocus
          onChange={(e) => setUrlDraft(e.target.value)}
          onBlur={commitUrl}
          onKeyDown={onUrlKey}
          aria-label="Image URL"
        />
      )}

      {hint && !error && <span className="muted small field-hint">{hint}</span>}
      {error && <span className="field-error">{error}</span>}
    </div>
  );
}
